// Issue templates come LIVE from n3oltd/work/.github/ISSUE_TEMPLATE/*.yml
// (see hooks/use-issue-templates.ts) so the dialog can never drift from the
// contract intake.yml parses. This module holds the shape + the yml→template
// converter; only the Blank entry (not a yml) is defined here.

export type TemplateSection = {
  /** Exact form label — becomes the `###` heading intake.yml reads. */
  label: string
  required: boolean
  hint?: string
  placeholder?: string
}

export type IssueTemplate = {
  key: string
  name: string
  description: string
  titlePrefix: string
  /** GitHub native issue type name from the form's `type:`. */
  issueType: string | null
  /** Labels applied at creation (the form's `labels:`). */
  labels: string[]
  /** The form's intro markdown, shown as helper copy. */
  intro?: string
  /** Present when the form has an Area dropdown; options come from the yml. */
  area?: { label: string; options: string[]; required: boolean }
  /** Present when the form has a Client input. */
  client?: { label: string; required: boolean; hint?: string }
  sections: TemplateSection[]
  maintainersOnly?: boolean
}

export const BLANK_TEMPLATE: IssueTemplate = {
  key: 'blank',
  name: 'Blank issue',
  description: 'Free-form — no template structure',
  titlePrefix: '',
  issueType: null,
  labels: [],
  sections: [{ label: 'Description', required: false }],
  maintainersOnly: true,
}

type FormBodyItem = {
  type?: string
  id?: string
  attributes?: {
    label?: string
    description?: string
    placeholder?: string
    options?: unknown[]
    value?: string
  }
  validations?: { required?: boolean }
}

type IssueForm = {
  name?: string
  description?: string
  title?: string
  type?: string
  labels?: unknown[]
  body?: FormBodyItem[]
}

/** Maps a parsed GitHub issue-form yml onto the dialog's template shape. */
export function templateFromIssueForm(filename: string, form: IssueForm): IssueTemplate | null {
  if (!form?.name) return null
  const key = filename.replace(/\.ya?ml$/, '')
  const template: IssueTemplate = {
    key,
    name: form.name,
    description: form.description ?? '',
    titlePrefix: form.title ?? '',
    issueType: form.type ?? null,
    labels: (form.labels ?? []).map(String),
    sections: [],
  }

  for (const item of form.body ?? []) {
    const label = item.attributes?.label
    switch (item.type) {
      case 'markdown':
        if (!template.intro && item.attributes?.value) {
          template.intro = item.attributes.value.trim()
        }
        break
      case 'dropdown':
        if (item.id === 'area' && label) {
          template.area = {
            label,
            options: (item.attributes?.options ?? []).map(String),
            required: item.validations?.required ?? false,
          }
        }
        break
      case 'input':
        if (item.id === 'client' && label) {
          template.client = {
            label,
            required: item.validations?.required ?? false,
            hint: item.attributes?.description,
          }
        }
        break
      case 'textarea':
        if (label) {
          template.sections.push({
            label,
            required: item.validations?.required ?? false,
            hint: item.attributes?.description,
            placeholder: item.attributes?.placeholder,
          })
        }
        break
    }
  }

  return template
}
